import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  InputLabel,
  Fade,
} from "@mui/material";
import { Link, useNavigate } from "react-router";
import { useSnackbar } from "notistack";
import BrandCarousel from "../login/components/BrandCarousel";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import { resetState, requestResetToken } from "../../redux/slices/loginSlice";

const ForgotPasswordForm = () => {
  const [fadeIn, setFadeIn] = useState(false);

  const { loading, resetTokenSuccess, resetTokenError } = useAppSelector(
    (state) => state.login,
  ) as any;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setFadeIn(true);

    if (resetTokenSuccess) {
      enqueueSnackbar("Password reset link sent to your email", {
        variant: "success",
      });
      setFadeIn(false);
      setTimeout(() => {
        navigate("/login");
      }, 1000);
    }

    if (resetTokenError) {
      enqueueSnackbar(resetTokenError, { variant: "error" });
    }
  }, [
    dispatch,
    setFadeIn,
    navigate,
    resetTokenSuccess,
    resetTokenError,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  const handleSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      const email = e.target.email.value;

      dispatch(requestResetToken({ email }));
    },
    [dispatch],
  );

  const handleBackToLogin = useCallback(
    (e: any) => {
      e.preventDefault();

      setFadeIn(false);

      dispatch(resetState());

      setTimeout(() => {
        navigate("/login");
      }, 1000);
    },
    [dispatch, navigate, setFadeIn],
  );

  return (
    <Fade in={fadeIn}>
      <Grid
        sx={{ width: "100vw", height: "100vh", overflow: "auto" }}
        container
      >
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{ display: "flex", alignItems: "center", p: 2 }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              maxWidth: 350,
              mx: "auto",
              "& .MuiOutlinedInput-input": {
                p: "4.5px 14px",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
              },
              "& .MuiTextField-root": {
                mt: 0,
              },
              "& .MuiButton-root": {
                borderRadius: 0,
                p: "2.75px 15px",
              },
            }}
          >
            <BrandCarousel />
            <Typography variant="h4" sx={{ fontSize: 24 }} gutterBottom>
              Forgot Your Password?
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: 15, mb: 2 }}
              gutterBottom
            >
              Provide your email address to receive a password reset link.
            </Typography>

            <InputLabel htmlFor="email" sx={{ fontSize: 14 }}>
              Email
            </InputLabel>
            <TextField
              id="email"
              type="email"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              // autoComplete="current-password"
              disabled={loading || resetTokenSuccess}
              error={resetTokenError?.toLowerCase()?.includes("email")}
              placeholder="Enter your email address"
            />
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              color="primary"
              fullWidth
              type="submit"
              disabled={loading || resetTokenSuccess}
            >
              Request Password Reset Link
            </Button>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ mt: 2, fontSize: 14 }}
              >
                Or go back to{" "}
                <Link
                  onClick={handleBackToLogin}
                  to="/login"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    fontWeight: "bold",
                    fontFamily: "Brandon_Grotesque_bold",
                  }}
                >
                  Log In
                </Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{
            display: { xs: "none", md: "block" },
            height: "100vh",
            overflow: "hidden",
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
              src="/forgot_reset_img.jpg"
              alt="Logo"
            />
          </Box>
        </Grid>
      </Grid>
    </Fade>
  );
};

export default ForgotPasswordForm;
